import { useCallback, useMemo } from 'react'

import { useWindowSize } from './use-window-size'

const BREAKPOINTS = {
  mobile: 0,
  smTablet: 480,
  tablet: 768,
  lgTablet: 992,
  desktop: 1280,
}

/**
 * For tailwind, we use the following breakpoints:
 * - mobile: 0px
 * - smTablet(xs): 481px
 * - tablet(md): 769px
 * - lgTablet(lg): 992px
 * - desktop(xl): 1280px
 *
 * For the app, we use the following breakpoints:
 */

export type BreakpointType = keyof typeof BREAKPOINTS

interface BpSwitchProps<T> {
  // desktop (xl)
  xl?: T
  // lgTablet (lg)
  lg?: T
  // tablet (md)
  md?: T
  // smTablet (xs)
  xs?: T
  // default (mobile)
  default: T
}

export default function useBreakpoint() {
  const { width } = useWindowSize()

  const breakpoint: BreakpointType = useMemo(() => {
    if (width < BREAKPOINTS.smTablet) return 'mobile'
    if (width < BREAKPOINTS.tablet) return 'smTablet'
    if (width < BREAKPOINTS.lgTablet) return 'tablet'
    if (width < BREAKPOINTS.desktop) return 'lgTablet'
    return 'desktop'
  }, [width])

  const bpSwitch = useCallback(
    <T>({ xl, lg, md, xs, default: mobile }: BpSwitchProps<T>) => {
      if (width >= BREAKPOINTS.desktop && xl) return xl
      if (width >= BREAKPOINTS.lgTablet && lg) return lg
      if (width >= BREAKPOINTS.tablet && md) return md
      if (width >= BREAKPOINTS.smTablet && xs) return xs
      return mobile
    },
    [width],
  )

  const isMobile = breakpoint === 'mobile'
  const isSmTablet = breakpoint === 'smTablet'
  const isTablet = breakpoint === 'tablet'
  const isLgTablet = breakpoint === 'lgTablet'
  const isDesktop = breakpoint === 'desktop'

  const isSmTabletOrSmaller = isMobile || isSmTablet
  const isTabletOrSmaller = isSmTabletOrSmaller || isTablet
  const isLgTabletOrSmaller = isTabletOrSmaller || isLgTablet

  return {
    breakpoint,
    isMobile,
    isSmTablet,
    isTablet,
    isLgTablet,
    isDesktop,
    isSmTabletOrSmaller,
    isTabletOrSmaller,
    isLgTabletOrSmaller,
    /** Works similar to tailwind's breakpoints.
     * E.g. bpSwitch({ default: 25, xs: 20 }) will return 25 if the width is less than 480px.
     * Otherwise, it will return 20. */
    bpSwitch,
  }
}
